import React, { useMemo, useState } from 'react';
import Modal from '../../../ui-kit/Modal';
import { Search, Download, Eye, RotateCcw, LifeBuoy } from 'lucide-react';
import { csvCell } from '../../../shared/utils/csv';
import { resolveOrderProduct } from '../logic/productName';
import QuickCallButton from './QuickCallButton';

const fmtDate = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('en-IN'); };
const skusOf = (o) => Array.isArray(o.items) ? o.items.map(it => it.sku || it.productId).filter(Boolean).join(', ') : '';
const qtyOf = (o) => Array.isArray(o.items) ? o.items.reduce((s, it) => s + (Number(it.quantity) || 1), 0) : '';
const statusLc = (o) => String(o.status || '').toLowerCase();

const FILTERS = ['All', 'COD', 'Prepaid', 'Delivered', 'Pending', 'Shipped', 'RTO', 'Refunded'];

const matchesFilter = (o, f) => {
    const s = statusLc(o);
    const pay = String(o.paymentMode || '').toLowerCase();
    switch (f) {
        case 'All': return true;
        case 'COD': return /cod|cash/.test(pay);
        case 'Prepaid': return /prepaid|online|paid|razorpay|cashfree/.test(pay);
        case 'Delivered': return s.includes('deliver');
        case 'Pending': return !s || s === 'pending';
        case 'Shipped': return s.includes('ship');
        case 'RTO': return s.includes('rto') || s.includes('return');
        case 'Refunded': return s.includes('refund');
        default: return true;
    }
};

// Read-only full order history for one customer. Actions: View / Reorder /
// Complaint / Call. Data = customer's nested order snapshots.
const CustomerOrderHistoryModal = ({ isOpen, onClose, customer, orders = [], productsMap = {}, onView, onReorder, onComplaint }) => {
    const [filter, setFilter] = useState('All');
    const [search, setSearch] = useState('');

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return orders
            .filter(o => matchesFilter(o, filter))
            .filter(o => !q || resolveOrderProduct(o, productsMap).toLowerCase().includes(q)
                || String(o.orderId || '').toLowerCase().includes(q) || skusOf(o).toLowerCase().includes(q))
            .sort((a, b) => new Date(b.orderDate || b.createdAt || 0) - new Date(a.orderDate || a.createdAt || 0));
    }, [orders, filter, search, productsMap]);

    const exportCsv = () => {
        const header = ['Date', 'Order ID', 'Product', 'SKU', 'Quantity', 'Amount', 'Payment Mode', 'Courier', 'Status', 'Delivery Status'];
        const lines = [header, ...rows.map(o => [
            fmtDate(o.orderDate || o.createdAt), o.orderId, resolveOrderProduct(o, productsMap), skusOf(o), qtyOf(o),
            o.amount || 0, o.paymentMode || '', o.courier || '', o.status || '', o.status || '',
        ])];
        const blob = new Blob(['﻿' + lines.map(r => r.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
        a.download = `orders-${customer?.primaryMobile || 'customer'}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(a.href);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Order History — ${customer?.name || ''} (${orders.length})`} className="max-w-6xl">
            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    {FILTERS.map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-lg border ${filter === f ? 'bg-purple-600 text-white border-purple-600' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}>{f}</button>
                    ))}
                    <div className="relative ml-auto">
                        <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Product / Order / SKU…"
                            className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm w-56" />
                    </div>
                    <button onClick={exportCsv} className="text-xs text-purple-600 flex items-center gap-1"><Download size={13} /> Export</button>
                </div>

                <div className="overflow-auto max-h-[60vh] border border-slate-100 rounded-lg">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                            <tr>
                                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Order ID</th><th className="px-3 py-2">Product</th>
                                <th className="px-3 py-2">SKU</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2">Payment</th><th className="px-3 py-2">Courier</th><th className="px-3 py-2">AWB</th>
                                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.length === 0 ? <tr><td colSpan={12} className="p-8 text-center text-slate-400">No orders match.</td></tr> :
                                rows.map((o, i) => (
                                    <tr key={o.orderId || i} className="hover:bg-slate-50">
                                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(o.orderDate || o.createdAt)}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{o.orderId}</td>
                                        <td className="px-3 py-2 max-w-[200px] truncate" title={resolveOrderProduct(o, productsMap)}>{resolveOrderProduct(o, productsMap)}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{skusOf(o) || '—'}</td>
                                        <td className="px-3 py-2">{qtyOf(o) || '—'}</td>
                                        <td className="px-3 py-2 text-right font-medium">₹{Number(o.amount) || 0}</td>
                                        <td className="px-3 py-2 text-xs">{o.paymentMode || '—'}</td>
                                        <td className="px-3 py-2 text-xs">{o.courier || '—'}</td>
                                        <td className="px-3 py-2 text-xs font-mono">{o.awb || '—'}</td>
                                        <td className="px-3 py-2 text-xs">{o.status || 'Pending'}</td>
                                        <td className="px-3 py-2 text-xs">{o.source || '—'}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <button onClick={() => onView?.(o)} title="View order" className="p-1.5 rounded text-indigo-600 hover:bg-indigo-50"><Eye size={14} /></button>
                                            <button onClick={() => onReorder?.(o)} title="Reorder" className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"><RotateCcw size={14} /></button>
                                            <button onClick={() => onComplaint?.(o)} title="Add complaint" className="p-1.5 rounded text-amber-600 hover:bg-amber-50"><LifeBuoy size={14} /></button>
                                            <QuickCallButton mobile={customer?.primaryMobile || customer?.mobile} customerName={customer?.name}
                                                orderId={o.orderId} sourceModule="customer_profile" callType="repeat_order_call"
                                                className="inline-block p-1.5 rounded text-green-600 hover:bg-green-50" iconSize={14} />
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
};

export default CustomerOrderHistoryModal;
